import React, { useState } from "react";
import { 
  X, 
  MapPin, 
  Info, 
  ShieldCheck, 
  Droplets, 
  Plus, 
  Loader2,
  HardHat
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";

export default function AddSiteModal({ isOpen, onClose, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    site_name: "",
    location: "",
    ro_code: "",
    sales_area: "",
    pump_count: "",
    tank_capacity: "",
    status: "construction"
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from("sites")
        .insert([formData]);

      if (error) throw error;

      toast.success("New site registered successfully!");
      onRefresh(); // Trigger data refresh in parent
      onClose();   // Close modal
    } catch (err) {
      toast.error(err.message || "Failed to create site.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal Content (Slide-over) */}
      <div className="relative w-full max-w-xl h-full bg-white dark:bg-slate-950 shadow-2xl animate-in slide-in-from-right duration-500 overflow-y-auto">
        
        {/* Modal Header */}
        <div className="sticky top-0 z-20 bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-50 dark:bg-blue-900/20 text-[#0054A6] rounded-xl flex items-center justify-center">
              <Plus size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">New Site Setup</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Register HPCL Asset</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          
          {/* Section: Basic Info */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-xs font-black text-[#0054A6] uppercase tracking-widest">
              <Info size={14} /> Basic Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <FormInput 
                label="Site Name (e.g., Shree Fill Station)" 
                required 
                value={formData.site_name}
                onChange={(val) => setFormData({...formData, site_name: val})}
              />
              <FormInput 
                label="Location (City/District)" 
                required 
                icon={<MapPin size={14}/>}
                value={formData.location}
                onChange={(val) => setFormData({...formData, location: val})}
              />
            </div>
          </div>

          {/* Section: HPCL Specific Details */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-xs font-black text-[#EE3124] uppercase tracking-widest">
              <HardHat size={14} /> HPCL Technical Data
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput 
                label="RO" 
                placeholder="e.g. Lucknnow RO"
                value={formData.ro_code}
                onChange={(val) => setFormData({...formData, ro_code: val})}
              />
              <FormInput 
                label="Sales Area" 
                placeholder="e.g. Lucknow-1"
                value={formData.sales_area}
                onChange={(val) => setFormData({...formData, sales_area: val})}
              />
              <FormInput 
                label="Pump Count (DU)" 
                type="number"
                icon={<Droplets size={14}/>}
                value={formData.pump_count}
                onChange={(val) => setFormData({...formData, pump_count: val})}
              />
              <FormInput 
                label="Tank Capacity" 
                placeholder="e.g. 20KL + 15KL"
                value={formData.tank_capacity}
                onChange={(val) => setFormData({...formData, tank_capacity: val})}
              />
            </div>
          </div>

          {/* Section: Compliance & Status */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
              <ShieldCheck size={14} /> Project Status
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Initial Status</label>
                <select 
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-xl text-sm font-bold dark:text-white outline-none focus:border-[#0054A6] transition-all"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="construction">Construction Phase</option>
                  <option value="active">Operational (Active)</option>
                  <option value="maintenance">Under Maintenance</option>
                </select>
              </div>
             
            </div>
          </div>

          {/* Form Footer */}
          <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 py-4 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl font-black uppercase text-xs hover:bg-slate-200 dark:hover:bg-slate-800 transition-all"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="flex-[2] py-4 bg-[#0054A6] text-white rounded-2xl font-black uppercase text-xs shadow-xl shadow-blue-100 dark:shadow-none hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
              Initialize Site
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Internal Input Helper Component
function FormInput({ label, required, type = "text", placeholder, icon, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
        {icon} {label} {required && <span className="text-[#EE3124]">*</span>}
      </label>
      <input 
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-xl text-sm font-bold dark:text-white outline-none focus:bg-white dark:focus:bg-black focus:border-[#0054A6] transition-all"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}